<template>
  <div class="admin-page">
    <div class="admin-page-inner">
      <div class="admin-page-header">
        <div class="admin-page-heading">
          <button
            type="button"
            class="admin-button--secondary admin-button--sm"
            title="Zurück zu Verträgen"
            @click="$router.push({ name: 'AdminDataVertraege' })"
          >
            <i class="fa fa-arrow-left mr-2" aria-hidden="true"></i>Zurück
          </button>
          <div>
            <span class="admin-kicker">Verträge</span>
            <h1 class="admin-page-title">Vertrag Details</h1>
            <p v-if="vertrag" class="admin-page-subtitle">{{ vertrag.vertragsbeschreibung }}</p>
          </div>
        </div>
      </div>

      <div>
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="text-center">
            <div
              class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-caspyellow mb-4">
            </div>
            <p class="text-gray-600 font-medium">Lade Vertragsdetails...</p>
          </div>
        </div>

        <div v-else-if="vertrag" class="space-y-6">
          <!-- Contract Details Section -->
          <div
            class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-200">
            <div class="px-6 py-4" style="background-color: #5f656e;">
              <h3 class="text-2xl font-bold text-white">Vertrag Details</h3>
            </div>
            <div class="p-6">
              <div class="mb-4">
                <p class="text-lg font-semibold text-purple-600 font-mono">APP-ID: APV-{{ vertrag.id }}</p>
              </div>
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div class="space-y-4">
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Vertragsbeschreibung</label>
                    <input v-model="editVertragData.vertragsbeschreibung" :disabled="!editing"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                      :class="{ 'bg-gray-100': !editing }" />
                  </div>
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Vertrag aktiv</label>
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" v-model="editVertragData.active" :disabled="!editing"
                        class="sr-only peer">
                      <div
                        class="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all after:shadow-md peer-checked:bg-green-600"
                        :class="{ 'opacity-50 cursor-not-allowed': !editing }"></div>
                      <span class="ml-3 text-sm font-medium text-gray-700">{{ editVertragData.active ? 'Aktiv' :
                        'Inaktiv' }}</span>
                    </label>
                  </div>
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Debitor</label>
                    <select v-model="editVertragData.debitor_id" :disabled="!editing"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                      :class="{ 'bg-gray-100': !editing }">
                      <option :value="null">Kein Debitor zugeordnet</option>
                      <option v-for="debitor in debitoren" :key="debitor.id" :value="debitor.id">
                        {{ debitor.unternehmensname }}
                      </option>
                    </select>
                    <div v-if="!editing && vertrag.debitor" class="mt-2 text-sm text-gray-600">
                      <span class="font-medium">{{ vertrag.debitor.name }}</span>
                      <span v-if="vertrag.debitor.unternehmensname" class="text-gray-500"> ({{
                        vertrag.debitor.unternehmensname }})</span>
                    </div>
                    <div v-if="!editing && !vertrag.debitor" class="mt-2 text-sm text-gray-400">
                      Kein Debitor zugeordnet
                    </div>
                  </div>
                </div>
                <div class="space-y-4">
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Zugeordnete Tarife</label>
                    <div v-if="editing"
                      class="border-2 border-gray-300 rounded-xl p-4 bg-gray-50 max-h-56 overflow-y-auto">
                      <div class="flex flex-wrap gap-2">
                        <button v-for="tarif in verrechnungstarife" :key="tarif.id" type="button"
                          class="admin-tab"
                          :class="{ 'admin-tab--active': editVertragData.tarifIds.includes(tarif.id) }"
                          @click="toggleTarif(editVertragData.tarifIds, tarif.id)">
                          {{ tarif.name }}
                        </button>
                      </div>
                    </div>
                    <div v-else class="flex flex-wrap gap-2">
                      <span v-for="tarif in vertrag.tarife" :key="tarif.id"
                        class="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-green-100 text-green-800">
                        {{ tarif.name }}
                      </span>
                      <span v-if="!vertrag.tarife || vertrag.tarife.length === 0" class="text-gray-400">
                        Keine Tarife zugeordnet
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex gap-3 justify-end pt-4">
                <button v-if="!editing" type="button" @click="startEdit"
                  class="admin-button--secondary">
                  Bearbeiten
                </button>
                <template v-else>
                  <button type="button" @click="cancelEdit"
                    class="admin-button--secondary">
                    Abbrechen
                  </button>
                  <button type="button" @click="saveVertrag"
                    class="admin-button--primary">
                    Speichern
                  </button>
                </template>
              </div>
            </div>
          </div>

          <!-- Einsatzorte & Einsatzstellen Section -->
          <div
            class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-200">
            <div class="px-6 py-4" style="background-color: #5f656e;">
              <h3 class="text-2xl font-bold text-white">Einsatzorte & Einsatzstellen</h3>
            </div>
            <div class="p-6">
              <!-- Add New Einsatzort -->
              <div class="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                <h4 class="text-lg font-semibold text-gray-800 mb-3">Neuer Einsatzort</h4>
                <p class="text-sm text-gray-600 mb-3">Hinweis: Codes werden automatisch vergeben. Bestehende Codes aus
                  der Migration bleiben erhalten.</p>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Beschreibung</label>
                    <input v-model="newEinsatzort.beschreibung"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                      placeholder="z.B. Hauptgebäude" />
                  </div>
                </div>
                <div class="flex justify-end mt-4">
                  <button type="button" @click="addEinsatzort" :disabled="!newEinsatzort.beschreibung.trim()"
                    class="admin-button--primary">
                    Einsatzort hinzufügen
                  </button>
                </div>
              </div>

              <!-- Einsatzorte List -->
              <div v-if="vertrag && vertrag.einsatzorte && vertrag.einsatzorte.length > 0" class="space-y-4">
                <div v-for="einsatzort in vertrag.einsatzorte" :key="einsatzort.id"
                  class="border-2 border-gray-200 rounded-xl p-4 bg-gray-50">
                  <div class="flex items-start justify-between mb-3">
                    <div class="flex items-start gap-3">
                      <svg class="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <div>
                        <input
                          v-if="editingEinsatzortId === einsatzort.id"
                          v-model="editEinsatzortBeschreibung"
                          class="w-full border border-gray-300 rounded-lg px-3 py-2 font-semibold focus:border-caspyellow focus:outline-none"
                          aria-label="Einsatzort-Beschreibung"
                        />
                        <h5 v-else class="text-lg font-bold text-gray-800">{{ einsatzort.einsatzort_beschreibung }}</h5>
                        <p class="text-sm font-semibold text-blue-600 font-mono">APP-ID: APO-{{ einsatzort.id }}</p>
                        <p class="text-xs text-gray-500">Alter Code: {{ einsatzort.einsatzort_code }}</p>
                        <span class="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium"
                          :class="einsatzort.active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'">
                          {{ einsatzort.active !== false ? 'Aktiv' : 'Inaktiv' }}
                        </span>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-2 justify-end">
                      <template v-if="editingEinsatzortId === einsatzort.id">
                        <button type="button" @click="cancelEditEinsatzort"
                          class="admin-button--secondary admin-button--sm">
                          Abbrechen
                        </button>
                        <button type="button" @click="saveEinsatzort(einsatzort)"
                          :disabled="!editEinsatzortBeschreibung.trim()"
                          class="admin-button--primary admin-button--sm">
                          Speichern
                        </button>
                      </template>
                      <button v-else type="button" @click="startEditEinsatzort(einsatzort)"
                        class="admin-button--secondary admin-button--sm">
                        Bearbeiten
                      </button>
                      <button type="button" @click="deleteEinsatzort(einsatzort)"
                        class="admin-button--danger admin-button--sm">
                        Löschen
                      </button>
                      <button type="button" @click="toggleEinsatzortActive(einsatzort)"
                        class="admin-button--secondary admin-button--sm">
                        {{ einsatzort.active !== false ? 'Deaktivieren' : 'Aktivieren' }}
                      </button>
                    </div>
                  </div>

                  <!-- Add New Einsatzstelle -->
                  <div class="ml-9 mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <h6 class="text-sm font-semibold text-gray-700 mb-2">Neue Einsatzstelle</h6>
                    <p class="text-xs text-gray-600 mb-2">Hinweis: Der Code wird automatisch vergeben.</p>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              
                      <div class="flex gap-2">
                        <input
                          :value="newEinsatzstelle[einsatzort.id] ? newEinsatzstelle[einsatzort.id].beschreibung : ''"
                          @input="updateEinsatzstelleBeschreibung(einsatzort.id, $event.target.value)"
                          class="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none"
                          placeholder="Beschreibung (z.B. Empfang)" />
                        <button type="button" @click="addEinsatzstelle(einsatzort)"
                          :disabled="!newEinsatzstelle[einsatzort.id] || !newEinsatzstelle[einsatzort.id].beschreibung.trim()"
                          class="admin-button--primary admin-button--sm">
                          Hinzufügen
                        </button>
                      </div>
                    </div>
                    <div class="mt-2">
                      <input
                        :value="newEinsatzstelle[einsatzort.id] ? newEinsatzstelle[einsatzort.id].registration_link : ''"
                        @input="updateEinsatzstelleRegistrationLink(einsatzort.id, $event.target.value)"
                        class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none"
                        placeholder="Registrierungslink (optional)" />
                      <p class="text-xs text-gray-500 mt-1">Optional: Link für Mitarbeiterregistrierung (nur in seltenen Fällen erforderlich)</p>
                    </div>
                  </div>

                  <!-- Einsatzstellen List -->
                  <div v-if="einsatzort.einsatzstellen && einsatzort.einsatzstellen.length > 0" class="ml-9 space-y-2">
                    <div v-for="stelle in einsatzort.einsatzstellen" :key="stelle.id"
                      class="bg-white border border-gray-200 rounded-lg">
                      <div class="flex items-center justify-between p-3">
                        <div class="flex items-center gap-2">
                          <svg class="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <div>
                            <input
                              v-if="editingEinsatzstelleId === stelle.id"
                              v-model="editEinsatzstelleBeschreibung"
                              class="w-full border border-gray-300 rounded-lg px-3 py-2 font-medium focus:border-caspyellow focus:outline-none"
                              aria-label="Einsatzstellen-Beschreibung"
                            />
                            <div v-else class="font-medium text-gray-800">{{ stelle.einsatzstelle_beschreibung }}</div>
                            <div class="text-sm font-semibold text-green-600 font-mono">APP-ID: APS-{{ stelle.id }}
                            </div>
                            <div class="text-xs text-gray-500">Alter Code: {{ stelle.einsatzstelle_code }}</div>
                            <span class="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium"
                              :class="stelle.active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'">
                              {{ stelle.active !== false ? 'Aktiv' : 'Inaktiv' }}
                            </span>
                            <div v-if="stelle.registration_link" class="text-xs text-blue-600 mt-1">
                              <i class="fa fa-link mr-1"></i>Registrierungslink vorhanden
                            </div>
                          </div>
                        </div>
                        <div class="flex flex-wrap gap-2 justify-end">
                          <template v-if="editingEinsatzstelleId === stelle.id">
                            <button type="button" @click="cancelEditEinsatzstelle"
                              class="admin-button--secondary admin-button--sm">
                              Abbrechen
                            </button>
                            <button type="button" @click="saveEinsatzstelle(einsatzort, stelle)"
                              :disabled="!editEinsatzstelleBeschreibung.trim()"
                              class="admin-button--primary admin-button--sm">
                              Speichern
                            </button>
                          </template>
                          <button v-else type="button" @click="startEditEinsatzstelle(stelle)"
                            class="admin-button--secondary admin-button--sm">
                            Bearbeiten
                          </button>
                          <button type="button" @click="toggleRegistrationLink(stelle)"
                            class="admin-button--secondary admin-button--sm"
                            title="Registrierungslink bearbeiten">
                            <i class="fa fa-link mr-1"></i>Reg-Link
                          </button>
                          <button type="button" @click="toggleAccessCodes(stelle)"
                            class="admin-button--secondary admin-button--sm"
                            title="Zugangscodes anzeigen/verbergen">
                            <i class="fa fa-key mr-1"></i>Codes ({{ getAccessCodeCount(stelle.einsatzstelle_code) }})
                          </button>
                          <button type="button" @click="toggleParkingTickets(stelle)"
                            class="admin-button--secondary admin-button--sm"
                            title="Parktickets anzeigen/verbergen">
                            <i class="fa fa-car mr-1"></i>Parking ({{ getParkingTicketCount(stelle.einsatzstelle_code)
                            }})
                          </button>
                          <button type="button" @click="deleteEinsatzstelle(einsatzort, stelle)"
                            class="admin-button--danger admin-button--sm">
                            Löschen
                          </button>
                          <button type="button" @click="toggleEinsatzstelleActive(einsatzort, stelle)"
                            class="admin-button--secondary admin-button--sm">
                            {{ stelle.active !== false ? 'Deaktivieren' : 'Aktivieren' }}
                          </button>
                        </div>
                      </div>

                      <!-- Registration Link Section -->
                      <div v-if="showRegistrationLinkFor === stelle.id" class="border-t border-gray-200 p-3 bg-orange-50">
                        <h4 class="font-semibold text-gray-700 mb-3">Registrierungslink</h4>
                        <div class="space-y-3">
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Link URL</label>
                            <input 
                              v-model="editRegistrationLink.url"
                              type="text"
                              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500 focus:ring-opacity-20 focus:outline-none"
                              placeholder="https://example.com/register" />
                            <p class="text-xs text-gray-500 mt-1">Optional: Wird nur in seltenen Fällen benötigt</p>
                          </div>
                          <div class="flex gap-2 justify-end">
                            <button
                              type="button"
                              @click="cancelRegistrationLinkEdit"
                              class="admin-button--secondary admin-button--sm">
                              Abbrechen
                            </button>
                            <button
                              type="button"
                              @click="saveRegistrationLink(einsatzort, stelle)"
                              class="admin-button--primary admin-button--sm">
                              Speichern
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Access Codes Section -->
                      <div v-if="showAccessCodesFor === stelle.id" class="border-t border-gray-200 p-3 bg-gray-50">
                        <div class="flex justify-between items-center mb-3">
                          <h4 class="font-semibold text-gray-700">Zugangscodes</h4>
                          <button type="button" @click="openBulkAddModal(einsatzort, stelle)"
                            class="admin-button--primary admin-button--sm">
                            <i class="fa fa-plus mr-1"></i>Codes hinzufügen
                          </button>
                        </div>

                        <div v-if="getAccessCodesForPlace(stelle.einsatzstelle_code).length > 0"
                          class="grid grid-cols-4 gap-2">
                          <div v-for="code in getAccessCodesForPlace(stelle.einsatzstelle_code)" :key="code.id"
                            class="flex items-center justify-between bg-white border border-gray-300 rounded px-3 py-2">
                            <span class="font-mono text-sm">{{ code.code }} <span v-if="code.claimedBy"> ({{
                                code.claimedBy
                                }})</span></span>
                            <button type="button" @click="deleteAccessCode(code)"
                              class="admin-button--ghost admin-button--icon admin-button--sm ml-2"
                              title="Code löschen" aria-label="Code löschen">
                              <i class="fa fa-trash text-xs text-red-600"></i>
                            </button>
                          </div>
                        </div>
                        <div v-else class="text-sm text-gray-500 italic">
                          Keine Zugangscodes vorhanden
                        </div>
                      </div>

                      <!-- Parking Tickets Section -->
                      <div v-if="showParkingTicketsFor === stelle.id" class="border-t border-gray-200 p-3 bg-purple-50">
                        <div class="flex justify-between items-center mb-3">
                          <h4 class="font-semibold text-gray-700">Parktickets</h4>
                          <button type="button" @click="openParkingTicketUploadModal(einsatzort, stelle)"
                            class="admin-button--primary admin-button--sm">
                            <i class="fa fa-upload mr-1"></i>Ticket hochladen
                          </button>
                        </div>

                        <div v-if="getParkingTicketsForPlace(stelle.einsatzstelle_code).length > 0"
                          class="grid grid-cols-3 gap-2">
                          <div v-for="ticket in getParkingTicketsForPlace(stelle.einsatzstelle_code)" :key="ticket.id"
                            class="flex items-center justify-between bg-white border border-gray-300 rounded px-3 py-2">
                            <span class="font-semibold text-sm">{{ ticket.licensePlate }}</span>

                            <div>
                              <button v-if="ticket.fileId" type="button" @click="viewTicketFile(ticket.fileId)"
                                class="admin-button--ghost admin-button--icon admin-button--sm ml-2" title="Datei anzeigen" aria-label="Datei anzeigen">
                                <i class="fa fa-eye text-xs text-purple-600"></i>
                              </button>
                              <button type="button" @click="deleteParkingTicket(ticket)"
                                class="admin-button--ghost admin-button--icon admin-button--sm ml-2" title="Ticket löschen" aria-label="Ticket löschen">
                                <i class="fa fa-trash text-xs text-red-600"></i>
                              </button>
                            </div>

                          </div>
                        </div>
                        <div v-else class="text-sm text-gray-500 italic">
                          Keine Parktickets vorhanden
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="ml-9 text-sm text-gray-500 italic">
                    Keine Einsatzstellen vorhanden
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-8 text-gray-500">
                Keine Einsatzorte vorhanden
              </div>
            </div>
          </div>

          <!-- Billing Overview Section -->
          <div
            class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-200">
            <div class="px-6 py-4" style="background-color: #5f656e;">
              <h3 class="text-2xl font-bold text-white">Abrechnungsübersicht</h3>
            </div>
            <div class="p-6">
              <!-- Month/Year Selection -->
              <div class="mb-6">
                <label class="block text-sm font-semibold text-gray-700 mb-2">Monat und Jahr auswählen</label>
                <div class="flex gap-4 items-center">
                  <select v-model="selectedMonth" @change="loadBillingData"
                    class="border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200">
                    <option value="1">Januar</option>
                    <option value="2">Februar</option>
                    <option value="3">März</option>
                    <option value="4">April</option>
                    <option value="5">Mai</option>
                    <option value="6">Juni</option>
                    <option value="7">Juli</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">Oktober</option>
                    <option value="11">November</option>
                    <option value="12">Dezember</option>
                  </select>
                  <select v-model="selectedYear" @change="loadBillingData"
                    class="border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200">
                    <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
                  </select>
                  <button type="button" @click="loadBillingData" :disabled="billingLoading"
                    class="admin-button--secondary">
                    <span v-if="billingLoading" class="flex items-center">
                      <div
                        class="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2">
                      </div>
                      Lade...
                    </span>
                    <span v-else>Laden</span>
                  </button>
                </div>
              </div>

              <!-- Billing Data -->
              <div v-if="billingLoading" class="text-center py-8">
                <div
                  class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-caspyellow">
                </div>
                <p class="text-gray-600 mt-2">Lade Abrechnungsdaten...</p>
              </div>

              <div v-else-if="billingData" class="space-y-6">
                <!-- Summary Cards -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div class="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div class="text-sm text-gray-600">Gesamtstunden</div>
                    <div class="text-2xl font-bold text-blue-600">{{ billingData.totalHours?.toFixed(2) || '0.00' }}h
                    </div>
                  </div>
                  <div class="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div class="text-sm text-gray-600">Mitarbeiter (Basis)</div>
                    <div class="text-2xl font-bold text-green-600">{{ formatCurrency(billingData.totalBaseAmount || 0)
                      }}</div>
                  </div>
                  <div class="bg-purple-50 border border-purple-200 rounded-xl p-4">
                    <div class="text-sm text-gray-600">Kunde (Verrechnung)</div>
                    <div class="text-2xl font-bold text-purple-600">{{ formatCurrency(billingData.totalBillingAmount ||
                      0) }}
                    </div>
                  </div>
                </div>

                <!-- Employee Breakdown -->
                <div v-if="billingData.employees && billingData.employees.length > 0">
                  <h4 class="text-lg font-bold text-gray-800 mb-4">Mitarbeiterübersicht</h4>
                  <div class="space-y-4">
                    <div v-for="employee in billingData.employees" :key="employee.id"
                      class="border-2 border-gray-200 rounded-xl p-4 bg-gray-50">
                      <div class="flex justify-between items-center mb-3">
                        <h5 class="font-semibold text-gray-800">{{ employee.name }} {{ employee.surname }}</h5>
                        <div class="text-right">
                          <div class="text-sm text-gray-600">Stunden: {{ employee.totalHours?.toFixed(2) || '0.00' }}h
                          </div>
                          <div class="text-sm font-semibold">Basis: {{ formatCurrency(employee.totalBaseAmount || 0) }}
                          </div>
                          <div class="text-sm font-semibold text-purple-600">Verrechnung: {{
                            formatCurrency(employee.totalBillingAmount || 0) }}</div>
                        </div>
                      </div>

                      <!-- Job Details -->
                      <div v-if="employee.jobs && employee.jobs.length > 0" class="mt-3">
                        <div class="bg-white border rounded-lg overflow-hidden">
                          <table class="w-full text-sm">
                            <thead class="bg-caspgrey border-b">
                              <tr>
                                <th class="py-2 px-3 text-left text-white">Datum</th>
                                <th class="py-2 px-3 text-left text-white">Einsatzstelle</th>
                                <th class="py-2 px-3 text-left text-white">Zeit</th>
                                <th class="py-2 px-3 text-left text-white">Schicht</th>
                                <th class="py-2 px-3 text-right text-white">Stunden</th>
                                <th class="py-2 px-3 text-right text-white">Basisrate / Stunde</th>
                                <th class="py-2 px-3 text-right text-white">Basisbetrag</th>
                                <th class="py-2 px-3 text-right text-white">Verrechnungsrate / Stunde</th>
                                <th class="py-2 px-3 text-right text-white">Verrechnungsbetrag</th>
                              </tr>
                            </thead>
                            <tbody>
                              <template v-for="job in employee.jobs" :key="job.id">
                                <!-- Main job row -->
                                <tr class="border-b hover:bg-gray-50 bg-gray-50">
                                  <td class="py-2 px-3 font-medium">{{ formatDate(job.Datum_von) }}</td>
                                  <td class="py-2 px-3">{{ job.Einsatzstelle_Beschreibung }}</td>
                                  <td class="py-2 px-3">
                                    <span v-if="job.Von_Uhrzeit && job.Bis_Uhrzeit" class="text-gray-800">
                                      {{ formatTime(job.Von_Uhrzeit) }} - {{ formatTime(job.Bis_Uhrzeit) }}
                                    </span>
                                    <span v-else-if="job.Soll_Von && job.Soll_Bis" class="text-red-600 font-medium">
                                      {{ formatTime(job.Soll_Von) }} - {{ formatTime(job.Soll_Bis) }}*
                                    </span>
                                    <span v-else class="text-gray-400">-</span>
                                  </td>
                                  <td class="py-2 px-3 text-gray-500 italic">Gesamt</td>
                                  <td class="py-2 px-3 text-right font-semibold">{{ job.hours?.toFixed(2) || '0.00' }}
                                  </td>
                                  <td class="py-2 px-3 text-right">-</td>
                                  <td class="py-2 px-3 text-right font-bold">{{ formatCurrency(job.baseAmount || 0) }}
                                  </td>
                                  <td class="py-2 px-3 text-right">-</td>
                                  <td class="py-2 px-3 text-right font-bold text-purple-600">{{
                                    formatCurrency(job.billingAmount || 0) }}</td>
                                </tr>
                                <!-- Shift breakdown rows -->
                                <tr v-for="shift in job.shifts" :key="`${job.id}-${shift.category}`"
                                  class="border-b hover:bg-gray-100 bg-white">
                                  <td class="py-2 px-3 pl-6"></td>
                                  <td class="py-2 px-3"></td>
                                  <td class="py-2 px-3"></td>
                                  <td class="py-2 px-3">
                                    <div class="font-medium text-gray-800">
                                      {{ shift.category.replace(/\s*\(.*?\)\s*/g, '') }}
                                      <span v-if="shift.isNight" class="text-blue-600 text-xs ml-1">Nacht</span>
                                      <span v-if="shift.isSunday" class="text-green-600 text-xs ml-1">Sonntag</span>
                                      <span v-if="shift.isHoliday" class="text-red-600 text-xs ml-1">Feiertag</span>
                                    </div>
                                  </td>
                                  <td class="py-2 px-3 text-right">{{ shift.hours?.toFixed(2) || '0.00' }}</td>
                                  <td class="py-2 px-3 text-right">

                                    <span>{{ formatCurrency(shift.effectiveRate || 0) }}</span>
                                  </td>
                                  <td class="py-2 px-3 text-right font-semibold">{{ formatCurrency(shift.baseAmount ||
                                    0) }}</td>
                                  <td class="py-2 px-3 text-right">
                                    <span>{{ formatCurrency(shift.effectiveBillingRate || 0) }}</span>
                                  </td>
                                  <td class="py-2 px-3 text-right font-semibold text-purple-600">{{
                                    formatCurrency(shift.billingAmount || 0) }}</td>
                                </tr>
                              </template>
                            </tbody>
                          </table>
                        </div>
                        <div class="text-xs text-gray-500 mt-2">
                          * = Geplante Zeit (keine Ist-Zeit erfasst)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="text-center py-8 text-gray-500">
                  <span>Keine Abrechnungsdaten für den ausgewählten Zeitraum gefunden.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="text-center py-16">
          <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-200 mb-4">
            <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p class="text-gray-500 text-lg font-medium">Vertrag nicht gefunden</p>
        </div>
      </div>
    </div>

    <AccessCodeBulkModal v-if="showBulkAddModal" :context="bulkAddContext" @close="showBulkAddModal = false"
      @save="handleAccessCodesSaved" />

    <ParkingTicketUploadModal v-if="showParkingTicketUploadModal" :context="parkingTicketUploadContext"
      @close="showParkingTicketUploadModal = false" @save="handleParkingTicketSaved" />
  </div>
</template>

<script>
import CASP from '../../CASP';
import AccessCodeBulkModal from '../../components/AccessCodeBulkModal.vue';
import ParkingTicketUploadModal from '../../components/ParkingTicketUploadModal.vue';

export default {
  components: {
    AccessCodeBulkModal,
    ParkingTicketUploadModal
  },
  data() {
    return {
      loading: true,
      billingLoading: false,
      vertrag: null,
      verrechnungstarife: [],
      debitoren: [],
      editing: false,
      editVertragData: {
        vertragsbeschreibung: '',
        navisionsync: false,
        active: true,
        tarifIds: [],
        debitor_id: null
      },
      billingData: null,
      selectedMonth: new Date().getMonth() + 1,
      selectedYear: new Date().getFullYear(),
      availableYears: [],
      newEinsatzort: {
        beschreibung: ''
      },
      newEinsatzstelle: {},
      editingEinsatzortId: null,
      editEinsatzortBeschreibung: '',
      editingEinsatzstelleId: null,
      editEinsatzstelleBeschreibung: '',
      accessCodes: [],
      showAccessCodesFor: null,
      showBulkAddModal: false,
      bulkAddContext: {},
      parkingTickets: [],
      showParkingTicketsFor: null,
      showParkingTicketUploadModal: false,
      parkingTicketUploadContext: {},
      showRegistrationLinkFor: null,
      editRegistrationLink: {
        url: '',
        einsatzortId: null,
        stelleId: null
      }
    };
  },
  computed: {
    vertragId() {
      return this.$route.params.id;
    }
  },
  methods: {
    toggleTarif(tarifIds, tarifId) {
      const index = tarifIds.indexOf(tarifId);
      if (index > -1) {
        tarifIds.splice(index, 1);
      } else {
        tarifIds.push(tarifId);
      }
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
      }).format(amount);
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE');
    },
    formatTime(timeString) {
      if (!timeString) return '';
      // Extract HH:MM from HH:MM:SS format if present
      return timeString.substring(0, 5);
    },
    loadVertrag() {
      this.loading = true;
      CASP.getVertrag(this.vertragId, (status, response) => {
        if (status === 200) {
          this.vertrag = response;
          this.editVertragData = {
            vertragsbeschreibung: response.vertragsbeschreibung,
            navisionsync: response.navisionsync,
            active: response.active !== false,
            tarifIds: response.tarife ? response.tarife.map(t => t.id) : [],
            debitor_id: response.debitor_id || null
          };
        } else {
          console.error('Fehler beim Laden des Vertrags:', response);
          alert('Fehler beim Laden des Vertrags: ' + (response?.error || response));
        }
        this.loading = false;
      });
    },
    loadTarife() {
      CASP.getTarife((status, response) => {
        if (status === 200) {
          this.verrechnungstarife = response.verrechnungstarife;
        } else {
          console.error('Fehler beim Laden der Tarife:', response);
        }
      });
    },
    loadDebitoren() {
      CASP.getKontakte(null, null, (status, response) => {
        if (status === 200) {
          this.debitoren = response.filter(k => k.is_debitor);
        } else {
          console.error('Fehler beim Laden der Debitoren:', response);
        }
      });
    },
    loadBillingData() {
      this.billingLoading = true;
      CASP.getContractBillingData(this.vertragId, this.selectedMonth, this.selectedYear, (status, response) => {
        if (status === 200) {
          this.billingData = response;
        } else {
          console.error('Fehler beim Laden der Abrechnungsdaten:', response);
          this.billingData = null;
        }
        this.billingLoading = false;
      });
    },
    startEdit() {
      this.editing = true;
    },
    cancelEdit() {
      this.editing = false;
      this.editVertragData = {
        vertragsbeschreibung: this.vertrag.vertragsbeschreibung,
        navisionsync: this.vertrag.navisionsync,
        active: this.vertrag.active !== false,
        tarifIds: this.vertrag.tarife ? this.vertrag.tarife.map(t => t.id) : [],
        debitor_id: this.vertrag.debitor_id || null
      };
    },
    saveVertrag() {
      CASP.updateVertrag(this.vertragId, this.editVertragData, (status, response) => {
        if (status === 200) {
          this.editing = false;
          this.loadVertrag();
        } else {
          console.error('Fehler beim Speichern des Vertrags:', response);
          alert('Fehler beim Speichern des Vertrags: ' + (response?.error || response));
        }
      });
    },
    initAvailableYears() {
      const currentYear = new Date().getFullYear();
      this.availableYears = [];
      for (let year = currentYear - 3; year <= currentYear + 1; year++) {
        this.availableYears.push(year);
      }
    },
    ensureEinsatzstelleObject(einsatzortId) {
      if (!this.newEinsatzstelle[einsatzortId]) {
        this.newEinsatzstelle[einsatzortId] = { beschreibung: '', registration_link: '' };
      }
    },
    updateEinsatzstelleBeschreibung(einsatzortId, value) {
      this.ensureEinsatzstelleObject(einsatzortId);
      this.newEinsatzstelle[einsatzortId].beschreibung = value;
    },
    updateEinsatzstelleRegistrationLink(einsatzortId, value) {
      this.ensureEinsatzstelleObject(einsatzortId);
      this.newEinsatzstelle[einsatzortId].registration_link = value;
    },
    addEinsatzort() {
      const beschreibung = this.newEinsatzort.beschreibung.trim();
      if (!beschreibung) return;

      CASP.createEinsatzort(this.vertragId, {
        einsatzort_beschreibung: beschreibung
      }, (status, response) => {
        if (status === 200) {
          this.newEinsatzort = { beschreibung: '' };
          this.loadVertrag();
        } else {
          console.error('Fehler beim Erstellen des Einsatzorts:', response);
          alert('Fehler beim Erstellen des Einsatzorts: ' + (response?.error || response));
        }
      });
    },
    startEditEinsatzort(einsatzort) {
      this.editingEinsatzortId = einsatzort.id;
      this.editEinsatzortBeschreibung = einsatzort.einsatzort_beschreibung;
    },
    cancelEditEinsatzort() {
      this.editingEinsatzortId = null;
      this.editEinsatzortBeschreibung = '';
    },
    saveEinsatzort(einsatzort) {
      const beschreibung = this.editEinsatzortBeschreibung.trim();
      if (!beschreibung) return;

      CASP.updateEinsatzort(this.vertragId, einsatzort.id, {
        einsatzort_beschreibung: beschreibung
      }, (status, response) => {
        if (status === 200) {
          this.cancelEditEinsatzort();
          this.loadVertrag();
        } else {
          console.error('Fehler beim Speichern des Einsatzorts:', response);
          alert('Fehler beim Speichern des Einsatzorts: ' + (response?.error || response));
        }
      });
    },
    toggleEinsatzortActive(einsatzort) {
      CASP.updateEinsatzort(this.vertragId, einsatzort.id, {
        active: einsatzort.active === false
      }, (status, response) => {
        if (status === 200) {
          this.loadVertrag();
        } else {
          alert('Fehler beim Ändern des Einsatzort-Status: ' + (response?.error || response));
        }
      });
    },
    deleteEinsatzort(einsatzort) {
      if (!confirm(`Möchten Sie den Einsatzort "${einsatzort.einsatzort_beschreibung}" wirklich löschen? Alle zugehörigen Einsatzstellen werden ebenfalls gelöscht.`)) return;

      CASP.deleteEinsatzort(this.vertragId, einsatzort.id, (status, response) => {
        if (status === 200) {
          this.loadVertrag();
        } else {
          console.error('Fehler beim Löschen des Einsatzorts:', response);
          alert('Fehler beim Löschen des Einsatzorts: ' + (response?.error || response));
        }
      });
    },
    addEinsatzstelle(einsatzort) {
      const data = this.newEinsatzstelle[einsatzort.id];
      const beschreibung = data?.beschreibung?.trim();
      if (!beschreibung) return;

      CASP.createEinsatzstelle(this.vertragId, einsatzort.id, {
        einsatzstelle_beschreibung: beschreibung,
        registration_link: data.registration_link || null
      }, (status, response) => {
        if (status === 200) {
          this.newEinsatzstelle[einsatzort.id] = { beschreibung: '', registration_link: '' };
          this.loadVertrag();
        } else {
          console.error('Fehler beim Erstellen der Einsatzstelle:', response);
          alert('Fehler beim Erstellen der Einsatzstelle: ' + (response?.error || response));
        }
      });
    },
    startEditEinsatzstelle(stelle) {
      this.editingEinsatzstelleId = stelle.id;
      this.editEinsatzstelleBeschreibung = stelle.einsatzstelle_beschreibung;
    },
    cancelEditEinsatzstelle() {
      this.editingEinsatzstelleId = null;
      this.editEinsatzstelleBeschreibung = '';
    },
    saveEinsatzstelle(einsatzort, stelle) {
      const beschreibung = this.editEinsatzstelleBeschreibung.trim();
      if (!beschreibung) return;

      CASP.updateEinsatzstelle(this.vertragId, einsatzort.id, stelle.id, {
        einsatzstelle_beschreibung: beschreibung
      }, (status, response) => {
        if (status === 200) {
          this.cancelEditEinsatzstelle();
          this.loadVertrag();
        } else {
          console.error('Fehler beim Speichern der Einsatzstelle:', response);
          alert('Fehler beim Speichern der Einsatzstelle: ' + (response?.error || response));
        }
      });
    },
    toggleEinsatzstelleActive(einsatzort, stelle) {
      CASP.updateEinsatzstelle(this.vertragId, einsatzort.id, stelle.id, {
        active: stelle.active === false
      }, (status, response) => {
        if (status === 200) {
          this.loadVertrag();
        } else {
          alert('Fehler beim Ändern des Einsatzstellen-Status: ' + (response?.error || response));
        }
      });
    },
    deleteEinsatzstelle(einsatzort, stelle) {
      if (!confirm(`Möchten Sie die Einsatzstelle "${stelle.einsatzstelle_beschreibung}" wirklich löschen?`)) return;

      CASP.deleteEinsatzstelle(this.vertragId, einsatzort.id, stelle.id, (status, response) => {
        if (status === 200) {
          this.loadVertrag();
        } else {
          console.error('Fehler beim Löschen der Einsatzstelle:', response);
          alert('Fehler beim Löschen der Einsatzstelle: ' + (response?.error || response));
        }
      });
    },
    loadAccessCodes() {
      CASP.getAccessCodes({}, (status, response) => {
        if (status === 200) {
          this.accessCodes = response;
        }
      });
    },
    toggleAccessCodes(stelle) {
      if (this.showAccessCodesFor === stelle.id) {
        this.showAccessCodesFor = null;
      } else {
        this.showAccessCodesFor = stelle.id;
        if (this.accessCodes.length === 0) {
          this.loadAccessCodes();
        }
      }
    },
    getAccessCodeCount(stelleCode) {
      return this.accessCodes.filter(c => c.einsatzstelle_code === stelleCode).length;
    },
    getAccessCodesForPlace(stelleCode) {
      return this.accessCodes.filter(c => c.einsatzstelle_code === stelleCode);
    },
    openBulkAddModal(einsatzort, stelle) {
      this.bulkAddContext = {
        einsatzstelle_code: stelle.einsatzstelle_code,
        einsatzstelle_name: stelle.einsatzstelle_beschreibung
      };
      this.showBulkAddModal = true;
    },
    handleAccessCodesSaved() {
      this.loadAccessCodes();
    },
    deleteAccessCode(code) {
      if (confirm(`Möchten Sie den Code "${code.code}" wirklich löschen?`)) {
        CASP.deleteAccessCode(code.id, (status) => {
          if (status === 200) {
            this.loadAccessCodes();
          } else {
            alert('Fehler beim Löschen des Codes');
          }
        });
      }
    },
    loadParkingTickets() {
      CASP.getParkingTickets({}, (status, response) => {
        if (status === 200) {
          this.parkingTickets = response;
        }
      });
    },
    toggleParkingTickets(stelle) {
      if (this.showParkingTicketsFor === stelle.id) {
        this.showParkingTicketsFor = null;
      } else {
        this.showParkingTicketsFor = stelle.id;
        if (this.parkingTickets.length === 0) {
          this.loadParkingTickets();
        }
      }
    },
    getParkingTicketCount(stelleCode) {
      return this.parkingTickets.filter(t => t.einsatzstelle_code === stelleCode).length;
    },
    getParkingTicketsForPlace(stelleCode) {
      return this.parkingTickets.filter(t => t.einsatzstelle_code === stelleCode);
    },
    openParkingTicketUploadModal(einsatzort, stelle) {
      this.parkingTicketUploadContext = {
        einsatzstelle_code: stelle.einsatzstelle_code,
        einsatzstelle_name: stelle.einsatzstelle_beschreibung
      };
      this.showParkingTicketUploadModal = true;
    },
    handleParkingTicketSaved() {
      this.loadParkingTickets();
    },
    deleteParkingTicket(ticket) {
      if (confirm(`Möchten Sie das Parkticket für "${ticket.licensePlate}" wirklich löschen?`)) {
        CASP.deleteParkingTicket(ticket.id, (status) => {
          if (status === 200) {
            this.loadParkingTickets();
          } else {
            alert('Fehler beim Löschen des Parktickets');
          }
        });
      }
    },
    viewTicketFile(fileId) {
      // Use OTL (One-Time Link) mechanism for secure file access
      CASP.getFileOTL(fileId, (status, response) => {
        if (status === 200 && response.link) {
          window.open(`${process.env.VUE_APP_API_URL}${response.link}`, '_blank');
        } else {
          alert('Fehler beim Laden der Datei');
        }
      });
    },
    toggleRegistrationLink(stelle) {
      if (this.showRegistrationLinkFor === stelle.id) {
        this.showRegistrationLinkFor = null;
        this.editRegistrationLink = { url: '', einsatzortId: null, stelleId: null };
      } else {
        this.showRegistrationLinkFor = stelle.id;
        this.editRegistrationLink = {
          url: stelle.registration_link || '',
          einsatzortId: stelle.einsatzort_id,
          stelleId: stelle.id
        };
      }
    },
    cancelRegistrationLinkEdit() {
      this.showRegistrationLinkFor = null;
      this.editRegistrationLink = { url: '', einsatzortId: null, stelleId: null };
    },
    saveRegistrationLink(einsatzort, stelle) {
      CASP.updateEinsatzstelle(this.vertragId, einsatzort.id, stelle.id, {
        registration_link: this.editRegistrationLink.url || null
      }, (status, response) => {
        if (status === 200) {
          this.showRegistrationLinkFor = null;
          this.editRegistrationLink = { url: '', einsatzortId: null, stelleId: null };
          this.loadVertrag();
        } else {
          console.error('Fehler beim Speichern des Registrierungslinks:', response);
          alert('Fehler beim Speichern des Registrierungslinks: ' + (response?.error || response));
        }
      });
    }
  },
  created() {
    document.getElementById("mainspinner").style.display = "none";
    if (localStorage) localStorage.setItem("last", `/admin/data/vertraege/${this.vertragId}`);
    this.initAvailableYears();
    this.loadVertrag();
    this.loadTarife();
    this.loadDebitoren();
    this.loadBillingData();
    this.loadAccessCodes();
    this.loadParkingTickets();
  }
};
</script>

<style scoped>
.hyphens-auto {
  hyphens: auto;
}
</style>
